import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";

import {
  type CaseView,
  anchorText,
  chooseAlternative,
  decideProposal,
  getCase,
  markNoSupport,
  message,
  ruleOnEvidence,
} from "./reviewClient";

function shown(value: unknown): string {
  return typeof value === "string" ? value : JSON.stringify(value);
}

/** One case, with every element of DESIGN-SPEC 24.1 on the page. */
export function ReviewCasePage() {
  const { caseId = "" } = useParams();
  const [view, setView] = useState<CaseView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rationale, setRationale] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let current = true;
    getCase(caseId)
      .then((found) => {
        if (current) {
          setView(found);
          setError(null);
        }
      })
      .catch((caught: unknown) => {
        if (current) {
          setError(message(caught));
        }
      });
    return () => {
      current = false;
    };
  }, [caseId, reloads]);

  /** Every action needs a rationale, and the page rereads the case after it. */
  function act(run: (why: string) => Promise<unknown>) {
    const why = rationale.trim();
    if (!why) {
      setActionError("State the rationale first.");
      return;
    }
    run(why)
      .then(() => {
        setActionError(null);
        setRationale("");
        setReloads((count) => count + 1);
      })
      .catch((caught: unknown) => setActionError(message(caught)));
  }

  if (error) {
    return <p role="alert">Could not load: {error}</p>;
  }
  if (!view) {
    return <p>Loading case.</p>;
  }
  const { claim, proposal, grade, impact } = view;
  const open = view.case_state === "open";
  const target = claim.target_object
    ? [claim.target_object, claim.target_attribute].filter(Boolean).join(".")
    : "None";
  const stance = (name: string) => view.evidence.filter((item) => item.stance === name);
  const evidenceList = (label: string, name: string) => (
    <>
      <h3>{label}</h3>
      {stance(name).length === 0 ? (
        <p>None.</p>
      ) : (
        <ul aria-label={label}>
          {stance(name).map((item) => (
            <li key={item.item_id}>
              {item.description} ({item.standing}, channel {item.channel_key}). Source version{" "}
              <code>{item.source_artifact_version_id ?? "not stated"}</code>. Authority:{" "}
              {item.authority_role ?? "not assessed"}. Proof limit:{" "}
              {item.proof_limit ?? "not stated"}.
              {item.applicable === false && " Does not apply to this claim."}{" "}
              {open && (
                <>
                  <button
                    type="button"
                    onClick={() => act((why) => ruleOnEvidence(caseId, item.item_id, "admit", why))}
                  >
                    Admit {item.description}
                  </button>{" "}
                  <button
                    type="button"
                    onClick={() =>
                      act((why) => ruleOnEvidence(caseId, item.item_id, "exclude", why))
                    }
                  >
                    Exclude {item.description}
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );

  return (
    <section aria-labelledby="case-title">
      <p>
        <Link to="/review">Back to the queue</Link>
      </p>
      <h1 id="case-title">{claim.statement}</h1>
      <p>
        Why it is in review: {view.entry_reason}. Lane {claim.lane}, status {claim.status}, case{" "}
        {view.case_state}.
      </p>
      <ul aria-label="Why it is here">
        {view.signals.map((signal) => (
          <li key={signal.name}>
            {signal.name}: {signal.detail}
          </li>
        ))}
      </ul>

      <h2>Proposal</h2>
      {proposal ? (
        <>
          <p>
            {shown(proposal.proposed_value)} ({proposal.output_kind}, {proposal.result_form})
          </p>
          <p>{proposal.reasoning}</p>
        </>
      ) : (
        <p>No proposal yet.</p>
      )}
      <p>Target: {target}</p>

      <h2>Alternatives</h2>
      {proposal && proposal.alternatives.length > 0 ? (
        <ul aria-label="Alternatives">
          {proposal.alternatives.map((alternative) => (
            <li key={alternative.id}>
              {shown(alternative.value)} ({alternative.standing}): {alternative.rationale}{" "}
              {open && (
                <button
                  type="button"
                  onClick={() =>
                    act((why) => chooseAlternative(caseId, proposal.id, alternative.id, why))
                  }
                >
                  Select {shown(alternative.value)}
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p>None.</p>
      )}

      <h2>Source regions</h2>
      {claim.anchors.length === 0 ? (
        <p role="status">This claim cites no source region.</p>
      ) : (
        <ul aria-label="Source regions">
          {claim.anchors.map((anchor) => (
            <li key={anchor.source_region_id}>
              {anchorText(anchor)}. Source version <code>{anchor.source_artifact_version_id}</code>,
              parsed document <code>{anchor.parsed_document_id}</code>, region{" "}
              <code>{anchor.source_region_id}</code>.{" "}
              <Link to="/source-explorer">Open the source explorer</Link>
            </li>
          ))}
        </ul>
      )}

      <h2>Evidence</h2>
      {evidenceList("Supporting evidence", "supporting")}
      {evidenceList("Contrary evidence", "contrary")}

      <h2>Context Boundary</h2>
      {claim.boundary ? (
        <dl aria-label="Context Boundary">
          {Object.entries(claim.boundary).map(([dimension, value]) => (
            <div key={dimension}>
              <dt>{dimension}</dt>
              <dd>{shown(value)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p role="status">This claim has no recorded Context Boundary.</p>
      )}

      <h2>Deterministic tests</h2>
      {view.tests.length === 0 ? (
        <p role="status">No tests have run on this claim.</p>
      ) : (
        <ul aria-label="Deterministic tests">
          {view.tests.map((test) => (
            <li key={test.result_id}>
              {test.test_key} v{test.version}: {test.outcome}. {test.reason}
              {test.profile_label && ` (${test.profile_label})`}
            </li>
          ))}
        </ul>
      )}

      <h2>Grade</h2>
      {grade ? (
        <p>
          {grade.grade}, under {grade.parameter_set_key} v{grade.parameter_set_version}. A ruling
          regrades this; a reviewer never sets it.
        </p>
      ) : (
        <p>Not graded.</p>
      )}

      <h2>Precedents</h2>
      {view.precedents.length === 0 ? (
        <p>No similar reviewed cases.</p>
      ) : (
        <ul aria-label="Precedents">
          {view.precedents.map((precedent) => (
            <li key={`${precedent.case_id}-${precedent.ruling_kind}`}>
              <Link to={`/review/${precedent.case_id}`}>
                {precedent.ruling_kind}, same {precedent.related_by}
              </Link>
              : {precedent.rationale}
            </li>
          ))}
        </ul>
      )}

      <h2>Potential impact</h2>
      <p>
        {impact.claims_with_same_target} other claims share this target,{" "}
        {impact.claims_with_same_mapping} share its mapping, and {impact.active_rules_on_mapping}{" "}
        active rules apply.
      </p>

      {open && (
        <>
          <h2>Decide</h2>
          <label>
            Rationale{" "}
            <textarea value={rationale} onChange={(event) => setRationale(event.target.value)} />
          </label>
          {actionError && <p role="alert">{actionError}</p>}
          {proposal && (
            <p>
              <button
                type="button"
                onClick={() => act((why) => decideProposal(caseId, proposal.id, "accept", why))}
              >
                Accept the proposal
              </button>{" "}
              <button
                type="button"
                onClick={() => act((why) => decideProposal(caseId, proposal.id, "reject", why))}
              >
                Reject the proposal
              </button>{" "}
              <button type="button" onClick={() => act((why) => markNoSupport(caseId, why))}>
                Mark no supported proposal
              </button>
            </p>
          )}
        </>
      )}

      <h2>Rulings</h2>
      {view.rulings.length === 0 ? (
        <p>None yet.</p>
      ) : (
        <ul aria-label="Rulings">
          {view.rulings.map((ruling) => (
            <li key={ruling.id}>
              {ruling.ruling_kind}: {ruling.rationale}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
